import React, { useEffect, useState, useRef } from 'react';
import CustomerSection from './CustomerSection';
import { FaUserCircle, FaList, FaPlusCircle } from 'react-icons/fa';
import ProductSection from './ProductSection';
import { useProposal } from '../../../context/proposalContext/ProposalContextProvider';
import { showAlert } from '../../../components';
import OffersTerm from './OffersTerm';
import { useParams, useNavigate } from 'react-router-dom';

const CreateProposal = () => {
  const [activeComponent, setActiveComponent] = useState('customerInfo');

  const {
    isValidData,
    proposal,
    handleSubmit,
    setProposal,
    initialState,
    getPropsalById,
    setIsForUpdate,
    setAlternativeValue,
    setShowAlternateProductSection,
    setEditProdIndex,
    handleSubmitWithoutLoader,
    copyProposal,
  } = useProposal();
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const isProposalUpdated = useRef(false); // Track if proposal is updated
  const previousQuoteProducts = useRef([]); // Track previous state of quoteProducts
  const previousProposalCopy = useRef(null); // Track a copy of the previous proposal
  const isApiCallInProgress = useRef(false); // Track if API call is in progress
  const debounceTimeout = useRef(null); // Track debounce timeout

  useEffect(() => {
    setProposal(initialState);
    setIsForUpdate(false);
    setAlternativeValue('No');
    setShowAlternateProductSection(false);
    setEditProdIndex(null);

    return () => {
      // Cleanup code if necessary
      setProposal(initialState);
      setIsForUpdate(false);
      setAlternativeValue('No');
      setShowAlternateProductSection(false);
      setEditProdIndex(null);
    };
  }, []);

  useEffect(() => {
    if (proposalId > 0) {
      getPropsalById(proposalId);
    } else {
      setProposal(initialState);
      setIsForUpdate(false);
      setAlternativeValue('No');
      setShowAlternateProductSection(false);
    }

    return () => {
      // Cleanup code if necessary
      setProposal(initialState);
      setIsForUpdate(false);
      setAlternativeValue('No');
      setShowAlternateProductSection(false);
    };
  }, [proposalId]);

  useEffect(() => {
    if (
      activeComponent === 'AddProduct' && // Ensure the active section is AddProduct
      proposal.quoteProducts &&
      proposal.quoteProducts.length > 0
    ) {
      const currentProposalCopy = {
        ...proposal,
        quoteProducts: proposal.quoteProducts.map(({ id, ...rest }) => ({
          ...rest, // Exclude `id` from comparison
        })),
      };

      const copyProposalNormalized = copyProposal
        ? {
            ...copyProposal,
            quoteProducts: copyProposal.quoteProducts.map(
              ({ id, ...rest }) => ({
                ...rest, // Exclude `id` from comparison
              }),
            ),
          }
        : null;

      if (
        (!previousProposalCopy.current || // If no previous copy exists
          JSON.stringify(previousProposalCopy.current) !==
            JSON.stringify(currentProposalCopy)) && // Compare normalized copies
        (!copyProposalNormalized || // If no copyProposal exists
          JSON.stringify(copyProposalNormalized) !==
            JSON.stringify(currentProposalCopy)) // Compare with copyProposal
      ) {
        clearTimeout(debounceTimeout.current); // Clear any existing timeout
        debounceTimeout.current = setTimeout(() => {
          if (!isApiCallInProgress.current) {
            isApiCallInProgress.current = true; // Set flag to true before API call
            previousProposalCopy.current = currentProposalCopy; // Update the tracked copy

            handleSubmitWithoutLoader({
              ...proposal,
              quoteProducts: [...proposal.quoteProducts], // Ensure quoteProducts is included
            }).finally(() => {
              isApiCallInProgress.current = false; // Reset flag after API call completes
            });
          }
        }, 300); // Debounce delay (300ms)
      }
    }
  }, [proposal.quoteProducts, activeComponent, copyProposal]);

  useEffect(() => {
    if (proposal.state === 'PENDING') {
      if (activeComponent === 'AddProduct') {
        setProposal((prevProposal) => ({
          ...prevProposal,
          state: 'SAVED',
        }));
      }
    }
  }, [activeComponent]);

  const handleSectionChange = async (section) => {
    // check is proposal and its copy is same or not
    if (proposal.quoteCode === '') {
      setProposal((prevProposal) => ({
        ...prevProposal,
        state: 'PENDING',
        quoteProducts: [],
        standardTotal: {},
        alternateTotal: {},
      }));
    } else if (proposal.quoteCode !== '') {
      if (
        proposal.state === 'WON' ||
        proposal.state === 'LOST' ||
        proposal.state === 'PIPELINE'
      ) {
        setProposal((prevProposal) => ({
          ...prevProposal,
        }));
      } else {
        setProposal((prevProposal) => ({
          ...prevProposal,
          state: 'SAVED',
        }));
      }
    } else {
      showAlert('error', 'An error occurred while creating proposal');
      return;
    }

    if (section === 'customerInfo' || isValidData(proposal)) {
      if (section !== 'AddProduct' && activeComponent === 'AddProduct') {
        if (proposal.quoteProducts.length === 0) {
          showAlert('error', 'Please add at least one product');
          return;
        }
        try {
          if (
            proposal.state === 'WON' ||
            proposal.state === 'LOST' ||
            proposal.state === 'PIPELINE'
          ) {
            try {
              await handleSubmit(proposal);
            } catch (error) {
              setActiveComponent('customerInfo');
              return;
            }
          } else {
            try {
              await handleSubmit({ ...proposal, state: 'SAVED' });
            } catch (error) {
              return;
            }
          }
        } catch (error) {
          showAlert('error', 'Failed to create proposal');
          return;
        }
      }

      if (section === 'AddProduct' && activeComponent === 'customerInfo') {
        try {
          await handleSubmit(proposal);
        } catch (error) {
          showAlert('error', 'Failed to create proposal');
          return;
        }
      }

      if (section === 'offerAndReview' && activeComponent === 'customerInfo') {
        if (
          proposal.quoteProducts.length === 0 ||
          proposal.quoteProducts[0].id === 0
        ) {
          showAlert('error', 'Please add at least one valid product');
          return;
        }
      }

      setActiveComponent(section);
    } else {
      showAlert('error', 'Please fill all the required fields');
    }
  };

  const handleNavigation = (path) => {
    if (
      window.confirm(
        'If you navigate, you will lose form data. Do you want to continue?',
      )
    ) {
      navigate(path);
    }
  };

  const renderComponent = () => {
    switch (activeComponent) {
      case 'customerInfo':
        return <CustomerSection proposalId={proposalId} />;
      case 'AddProduct':
        return <ProductSection />;
      case 'offerAndReview':
        return <OffersTerm />;
      default:
        return <CustomerSection />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className=" -ml-20 z-10 py-4 px-40 min-w-screen bg-gradient-to-r backdrop-blur-md from-[#ffb70024] to-[#ffb800] text-black text-2xl fixed top-0 w-full shadow-lg font-800  font-800">
        Create Proposal{' '}
        {proposal.quoteCode ? (
          <span className="text-base font-800">
            {' ['}
            {proposal.quoteCode}
            {'] '}
          </span>
        ) : (
          <></>
        )}
      </div>
      <div className="-ml-20 w-full bg-gray-200 h-2 fixed top-16 z-40">
        <div
          className={`h-full transition-all duration-500 ease-in-out z-50 ${
            activeComponent === 'customerInfo'
              ? 'bg-green-500 w-0'
              : activeComponent === 'AddProduct'
                ? 'bg-green-500 w-1/3'
                : activeComponent === 'offerAndReview'
                  ? 'bg-green-500 w-2/3'
                  : 'bg-green-500 w-0'
          }`}
        ></div>
      </div>
      <div className=" mx-5 mt-20 mb-20">{renderComponent()}</div>
      {/* Footer Buttons */}
      <div className="-ml-20 z-10 py-2 px-40 fixed bottom-0  min-w-full font-700 backdrop-blur-md bg-black/5 bg-opacity-30 flex justify-center gap-10 p-3 shadow-xl text-black ">
        <button
          className={`flex items-center justify-center gap-2 py-2 px-5 rounded-full text-base transition-transform transform hover:scale-110 font-400  
            ${
              activeComponent === 'customerInfo'
                ? 'bg-gradient-to-r from-pink-100 to-yellow-100'
                : 'bg-white'
            }`}
          onClick={() => handleSectionChange('customerInfo')}
        >
          <FaUserCircle size={24} />
          <span>Customer Information</span>
        </button>

        <button
          className={`flex items-center justify-center gap-2 py-2 px-5 rounded-full text-base transition-transform transform hover:scale-110 font-400 
            ${
              activeComponent === 'AddProduct'
                ? 'bg-gradient-to-r from-green-100 to-teal-100'
                : 'bg-white'
            }`}
          onClick={() => handleSectionChange('AddProduct')}
        >
          <FaList size={24} />
          <span>Add Products</span>
        </button>

        <button
          className={`flex items-center justify-center gap-2 py-2 px-5 rounded-full text-base transition-transform transform hover:scale-110 font-400  
            ${
              activeComponent === 'offerAndReview'
                ? 'bg-gradient-to-r from-indigo-100 to-purple-100'
                : 'bg-white'
            }`}
          onClick={() => handleSectionChange('offerAndReview')}
        >
          <FaPlusCircle size={24} />
          <span>Offer And Review</span>
        </button>
      </div>
    </div>
  );
};

export default CreateProposal;
